"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useState, useEffect, useMemo, useRef, useOptimistic, useTransition, useDeferredValue } from "react";
import { useAssets, useAssetsCount, useTrendingTags } from "@/hooks/useAssets";
import { useCategories } from "@/hooks/useCategories";
import { useTrendingAssets } from "@/hooks/useTrendingAssets";
import { useSponsoredAssets } from "@/hooks/useSponsoredAssets";
import { useAdTracking } from "@/hooks/useAdTracking";
import { useIsMobile } from "@/hooks/use-mobile";
import { PublicPageShell } from "@/components/layout/PublicPageShell";
import { BrowseFilters } from "@/components/browse/BrowseFilters";
import { BrowsePagination } from "@/components/browse/BrowsePagination";
import { BackToTopButton } from "@/components/browse/BackToTopButton";
import { ActiveFilters } from "@/components/browse/ActiveFilters";
import { BrowseAssetCard } from "@/components/browse/BrowseAssetCard";
import { BrowseProductCardSkeleton } from "@/components/browse/BrowseProductGridSkeleton";
import { BrowseEmptyState } from "@/components/browse/BrowseEmptyState";
import type { RestrictiveFilterHint } from "@/components/browse/BrowseEmptyState";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SubscriptionUpsellBanner } from "@/components/ui/subscription-upsell-banner";
import { SectionBackground } from "@/components/ui/SectionBackground";
import { PageError } from "@/components/ui/page-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/useDebounce";
import {
  trackSearch,
  trackSelectCategory,
  trackBrowseViewed,
  trackBrowseFilterApplied,
} from "@/lib/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import Search from "lucide-react/dist/esm/icons/search";
import PanelLeftClose from "lucide-react/dist/esm/icons/panel-left-close";
import PanelLeftOpen from "lucide-react/dist/esm/icons/panel-left-open";
import SlidersHorizontal from "lucide-react/dist/esm/icons/sliders-horizontal";
import X from "lucide-react/dist/esm/icons/x";
import Flame from "lucide-react/dist/esm/icons/flame";
import Code2 from "lucide-react/dist/esm/icons/code-2";
import { cn } from "@/lib/utils";
import {
  BROWSE_DEFAULTS,
  BROWSE_PAGE_SIZES,
  parseBrowseUrl,
  serializeBrowseUrl,
  toAssetFilters,
  getTopRestrictiveFilters,
  toggleBrowseTagList,
  type BrowseUrlState,
} from "@/lib/browse-url-state";

function browseHasActiveFilters(state: BrowseUrlState): boolean {
  return !!(
    state.category ||
    state.q.trim() ||
    state.tags.length > 0 ||
    state.sort !== BROWSE_DEFAULTS.sort ||
    state.page > 1 ||
    state.license !== "all" ||
    state.minPrice > BROWSE_DEFAULTS.minPrice ||
    state.maxPrice < BROWSE_DEFAULTS.maxPrice ||
    state.minRating > 0
  );
}

function TrendingTagsStrip({ onPickTag }: { onPickTag: (tag: string) => void }) {
  const { data: trendingTags, isLoading } = useTrendingTags(15);

  return (
    <div className="border-b border-border/20 bg-card/20 overflow-hidden">
      <div className="container flex items-center gap-2 sm:gap-3 py-2 sm:py-3">
        <div className="flex items-center gap-1.5 shrink-0">
          <Flame className="h-3.5 w-3.5 text-primary" />
          <span className="hidden sm:inline font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Trending:
          </span>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-none flex-1 min-w-0 items-center">
          {isLoading ? (
            <>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-7 w-20 rounded-full shrink-0" style={{ animationDelay: `${i * 50}ms` }} />
              ))}
            </>
          ) : (
            trendingTags?.map(({ tag }: { tag: string }) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => onPickTag(tag)}
                  className="shrink-0 rounded-full border border-primary/20 px-3 py-1 text-xs font-mono transition-colors hover:bg-primary/20 hover:border-primary/40 whitespace-nowrap"
                >
                  {tag}
                </button>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

function buildRestrictiveHints(
  state: BrowseUrlState,
  categoryName: string | null,
  applyPatch: (p: Partial<BrowseUrlState>) => void,
): RestrictiveFilterHint[] {
  const top = getTopRestrictiveFilters(state, categoryName);
  return top.map((c) => ({
    label: c.label,
    description: c.description,
    onRemove: () => {
      if (c.label === "Search") applyPatch({ q: "", page: 1 });
      else if (c.label === "Category") applyPatch({ category: null, page: 1 });
      else if (c.label === "License") applyPatch({ license: "all", page: 1 });
      else if (c.label === "Rating") applyPatch({ minRating: 0, page: 1 });
      else if (c.label === "Tags") applyPatch({ tags: [], page: 1 });
      else if (c.label === "Price")
        applyPatch({
          minPrice: BROWSE_DEFAULTS.minPrice,
          maxPrice: BROWSE_DEFAULTS.maxPrice,
          page: 1,
        });
    },
  }));
}

export default function Browse() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const isMobile = useIsMobile();

  const spKey = searchParams.toString();
  const committed = useMemo(() => parseBrowseUrl(new URLSearchParams(spKey)), [spKey]);

  const [optimistic, addOptimistic] = useOptimistic(committed, (prev, patch: Partial<BrowseUrlState>) => ({
    ...prev,
    ...patch,
  }));
  const [, startNav] = useTransition();

  const [searchInput, setSearchInput] = useState(committed.q);
  // True only when the user typed in THIS page's search box. It lets the
  // debounced URL-commit effect below distinguish a local edit from an external
  // `committed.q` change (e.g. the nav search bar's router.push to /browse?q=…).
  // Without it, an external nav-search update is immediately clobbered back to
  // this page's stale debounced value, so the nav search "never completes".
  const userEditedSearchRef = useRef(false);
  useEffect(() => {
    setSearchInput(committed.q);
  }, [committed.q]);

  const debouncedSearch = useDebounce(searchInput, 300);
  // useDeferredValue lets the input remain responsive while expensive memos/renders defer
  const deferredSearch = useDeferredValue(debouncedSearch);

  useEffect(() => {
    // Only commit the local search box to the URL. External URL changes (nav
    // search) flow in through committed.q + the sync effect above and must not
    // be overwritten here.
    if (!userEditedSearchRef.current) return;
    // Wait until the debounce catches up to the live input. An external
    // committed.q change re-runs this effect with a stale debounced value;
    // committing that would clobber the nav search back to the old query.
    if (debouncedSearch !== searchInput) return;
    // Edit has settled — consume the flag (even if no write is needed) so a
    // later external change can't be mistaken for a local edit.
    userEditedSearchRef.current = false;
    if (debouncedSearch === committed.q) return;
    router.replace(`${pathname}?${serializeBrowseUrl({ ...optimistic, q: debouncedSearch, page: 1 })}`, {
      scroll: false,
    });
  }, [debouncedSearch, searchInput, committed.q, optimistic, pathname, router]);

  // Data fetching follows the COMMITTED URL state, never the optimistic one.
  // `useOptimistic` only holds its patched value while the transition that
  // called `addOptimistic` is pending — and `router.replace()` is not an
  // awaitable, so that transition settles after a single render and reverts
  // `optimistic` back to `committed` *before* `useSearchParams` propagates the
  // new URL. Keying the query off `optimistic` therefore flips page/perPage/
  // sort back for a frame, and with `keepPreviousData` the stale first page is
  // what stays on screen — i.e. "changing page shows the same assets". The URL
  // is the source of truth, so the grid/count/pagination read `committed`;
  // `optimistic` is reserved for instant filter-control feedback only.
  const fetchState = useMemo(
    () => ({ ...committed, q: deferredSearch }),
    [committed, deferredSearch],
  );

  const filters = useMemo(() => toAssetFilters(fetchState, fetchState.q), [fetchState]);

  const { data: assets, isLoading, isFetching, error, refetch } = useAssets(filters, fetchState.page - 1, fetchState.perPage);
  const { data: totalCount } = useAssetsCount(filters);
  const { data: categories } = useCategories();
  const { data: trendingIds } = useTrendingAssets();

  const sponsoredPlacement = debouncedSearch ? "search" : optimistic.category ? "category" : "browse";
  const { data: sponsoredAssets } = useSponsoredAssets({
    placement: sponsoredPlacement,
    categorySlug: optimistic.category,
    limit: 4,
    enabled: true,
  });
  const { recordClick, recordImpressions } = useAdTracking();
  const impressionTrackedRef = useRef<string>("");

  // Real total from the count RPC. Until it resolves, fall back to "this page
  // is full → there's at least one more" so pagination stays usable.
  const pageItemCount = assets?.length ?? 0;
  const hasMore = pageItemCount === fetchState.perPage;
  const totalPages =
    totalCount !== undefined
      ? Math.max(1, Math.ceil(totalCount / fetchState.perPage))
      : hasMore
        ? committed.page + 1
        : committed.page;
  const totalAssets = totalCount ?? pageItemCount;

  // Sponsored assets render in their own row above the grid (page 1 only), so
  // the organic grid just filters out anything already shown there.
  const paginatedAssets = useMemo(() => {
    const organic = assets ?? ([] as any[]);
    if (committed.page !== 1 || !sponsoredAssets?.length)
      return organic.map((a) => ({ asset: a }));
    const sponsoredIds = new Set(sponsoredAssets.map((s) => s.id));
    return organic.filter((a) => !sponsoredIds.has(a.id)).map((a) => ({ asset: a }));
  }, [assets, committed.page, sponsoredAssets]);

  // Sponsored rows mapped to the card's Asset shape.
  const sponsoredCards = useMemo(
    () =>
      (sponsoredAssets ?? []).slice(0, 4).map((s) => ({
        campaignId: s.campaign_id,
        asset: {
          id: s.id,
          title: s.title,
          slug: s.slug,
          price: s.price,
          is_free: s.is_free,
          pricing_type: s.pricing_type,
          pwyw_minimum: s.pwyw_minimum,
          created_at: s.created_at,
          sale_price: s.sale_price,
          sale_starts_at: s.sale_starts_at,
          sale_ends_at: s.sale_ends_at,
          category: s.category,
          seller: s.seller,
          primary_image: s.primary_image,
          avg_rating: s.rating,
          review_count: s.review_count,
          download_count: 0,
          view_count: 0,
          featured: false,
        } as any,
      })),
    [sponsoredAssets],
  );

  useEffect(() => {
    if (!sponsoredAssets?.length || committed.page !== 1) return;
    const key = sponsoredAssets.map((s) => s.campaign_id).sort().join(",");
    if (impressionTrackedRef.current === key) return;
    impressionTrackedRef.current = key;
    recordImpressions(sponsoredAssets.map((s) => ({ campaignId: s.campaign_id, placement: sponsoredPlacement })));
  }, [sponsoredAssets, committed.page, sponsoredPlacement, recordImpressions]);

  const hasActiveFilters = browseHasActiveFilters(fetchState);

  const applyUrlPatch = (patch: Partial<BrowseUrlState>, trackKeys?: string[]) => {
    startNav(() => {
      addOptimistic(patch);
      const next: BrowseUrlState = {
        ...optimistic,
        ...patch,
        q: debouncedSearch,
      };
      if (patch.page === undefined) next.page = 1;
      router.replace(`${pathname}?${serializeBrowseUrl(next)}`, { scroll: false });
      if (trackKeys) {
        for (const k of trackKeys) {
          const v = (patch as Record<string, unknown>)[k];
          if (v !== undefined) trackBrowseFilterApplied(k, v as string | number);
        }
      }
      if (patch.category) trackSelectCategory(patch.category);
    });
  };

  const clearFilters = () => {
    startNav(() => {
      addOptimistic({
        ...BROWSE_DEFAULTS,
        q: "",
      });
      router.replace(pathname, { scroll: false });
      setSearchInput("");
    });
  };

  const categoryName = optimistic.category
    ? categories?.find((c) => c.slug === optimistic.category)?.name || null
    : null;

  const handlePageChange = (page: number) => {
    applyUrlPatch({ page });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (debouncedSearch) trackSearch(debouncedSearch, totalAssets);
  }, [debouncedSearch, totalAssets]);

  const viewedKey = useRef<string>("");
  useEffect(() => {
    if (isFetching) return;
    const key = `${spKey}|${totalAssets}`;
    if (viewedKey.current === key) return;
    viewedKey.current = key;
    trackBrowseViewed(
      {
        category: fetchState.category,
        sort: fetchState.sort,
        license: fetchState.license,
        q: fetchState.q || undefined,
      },
      totalAssets,
    );
  }, [isFetching, spKey, totalAssets, fetchState.category, fetchState.sort, fetchState.license, fetchState.q]);

  const [railCollapsed, setRailCollapsed] = useState(false);
  const [railPreferenceReady, setRailPreferenceReady] = useState(false);
  useEffect(() => {
    setRailCollapsed(localStorage.getItem("browse-rail-collapsed") === "true");
    setRailPreferenceReady(true);
  }, []);
  useEffect(() => {
    if (!railPreferenceReady) return;
    localStorage.setItem("browse-rail-collapsed", String(railCollapsed));
  }, [railCollapsed, railPreferenceReady]);

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const gridCols =
    railCollapsed || isMobile
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3";

  const restrictiveHints = hasActiveFilters
    ? buildRestrictiveHints(fetchState, categoryName, (p) => applyUrlPatch(p))
    : undefined;

  return (
    <>
      <PublicPageShell mainAriaLabel="Browse marketplace" bleed className="relative">
        {/* Banner constrained to the container width so it aligns with the
         * filters + grid below — without this the rounded card was
         * wider than the rest of the page on every viewport. */}
        <div className="container pt-4">
          <SubscriptionUpsellBanner targetTier="buyer_plus" context="browse" />
        </div>

        <section className="relative overflow-hidden border-b border-border/30 bg-card/30">
          <SectionBackground variant="grid" />
          <div className="relative z-10 container py-5 md:py-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="shrink-0">
                <h1 className="font-display text-2xl md:text-3xl font-bold">
                  {categoryName ? `Browse UEFN ${categoryName}` : "Browse UEFN Assets"}
                </h1>
              </div>
              <div className="flex-1 max-w-xl relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search UEFN assets, categories, creators…"
                  aria-label="Search assets"
                  value={searchInput}
                  onChange={(e) => {
                    userEditedSearchRef.current = true;
                    setSearchInput(e.target.value);
                  }}
                  className="pl-10 pr-20 h-10 rounded-lg bg-background border-border/50 focus-visible:ring-1 focus-visible:ring-primary/50 focus-visible:border-primary"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        applyUrlPatch({ q: "", page: 1 });
                      }}
                      className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {totalAssets > 0 && !isFetching && (
                    <span className="text-[10px] text-muted-foreground font-mono whitespace-nowrap hidden sm:inline">
                      {totalAssets} results
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="sticky top-0 z-30 bg-background border-b border-border">
          <div className="container py-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant={hasActiveFilters ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (isMobile) setShowMobileFilters(true);
                  else setRailCollapsed((c) => !c);
                }}
                className={cn(
                  "h-9 px-3 shrink-0",
                  isMobile && "h-10 px-4 font-medium",
                )}
                aria-label={railCollapsed ? "Expand filters" : "Collapse filters"}
                aria-expanded={!railCollapsed}
                aria-controls="browse-filter-rail"
              >
                {isMobile ? (
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                ) : railCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4 mr-1.5" />
                ) : (
                  <PanelLeftClose className="h-4 w-4 mr-1.5" />
                )}
                <span className="text-sm font-medium">Filters{hasActiveFilters ? " · active" : ""}</span>
              </Button>

              <div className="hidden sm:flex flex-1 min-w-0 items-center gap-2 text-sm text-muted-foreground font-mono">
                <span aria-live="polite">
                  {isFetching ? "Loading…" : `${totalAssets} results`}
                </span>
              </div>

              {/* Sort */}
              <Select value={committed.sort} onValueChange={(val) => applyUrlPatch({ sort: val, page: 1 }, ["sort"])}>
                <SelectTrigger
                  className="w-[140px] h-8 bg-card/50 border-border/50 focus:ring-primary text-xs shrink-0 ml-auto"
                  aria-label="Sort results"
                >
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="border-border/50">
                  <SelectItem value="relevance">Relevance</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="popular">Best sellers</SelectItem>
                  <SelectItem value="rating">Highest rated</SelectItem>
                  <SelectItem value="price_low">Price · Low to high</SelectItem>
                  <SelectItem value="price_high">Price · High to low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="hidden sm:flex flex-1 min-w-0 overflow-x-auto scrollbar-none">
              {hasActiveFilters && (
                <ActiveFilters
                  selectedCategory={optimistic.category}
                  categoryName={categoryName}
                  priceRange={[optimistic.minPrice, optimistic.maxPrice]}
                  minRating={optimistic.minRating}
                  license={optimistic.license}
                  search={debouncedSearch}
                  selectedTags={optimistic.tags}
                  onClearCategory={() => applyUrlPatch({ category: null }, ["category"])}
                  onClearPriceRange={() =>
                    applyUrlPatch(
                      { minPrice: BROWSE_DEFAULTS.minPrice, maxPrice: BROWSE_DEFAULTS.maxPrice },
                      ["minPrice", "maxPrice"],
                    )
                  }
                  onClearRating={() => applyUrlPatch({ minRating: 0 }, ["minRating"])}
                  onClearLicense={() => applyUrlPatch({ license: "all" }, ["license"])}
                  onClearSearch={() => {
                    setSearchInput("");
                    applyUrlPatch({ q: "" }, ["q"]);
                  }}
                  onRemoveTag={(tag) =>
                    applyUrlPatch({ tags: optimistic.tags.filter((t) => t !== tag), page: 1 })
                  }
                  onClearAll={clearFilters}
                />
              )}
            </div>

            {hasActiveFilters && (
              <div className="sm:hidden">
                <ActiveFilters
                  selectedCategory={optimistic.category}
                  categoryName={categoryName}
                  priceRange={[optimistic.minPrice, optimistic.maxPrice]}
                  minRating={optimistic.minRating}
                  license={optimistic.license}
                  search={debouncedSearch}
                  selectedTags={optimistic.tags}
                  onClearCategory={() => applyUrlPatch({ category: null })}
                  onClearPriceRange={() =>
                    applyUrlPatch({
                      minPrice: BROWSE_DEFAULTS.minPrice,
                      maxPrice: BROWSE_DEFAULTS.maxPrice,
                    })
                  }
                  onClearRating={() => applyUrlPatch({ minRating: 0 })}
                  onClearLicense={() => applyUrlPatch({ license: "all" })}
                  onClearSearch={() => {
                    setSearchInput("");
                    applyUrlPatch({ q: "" });
                  }}
                  onRemoveTag={(tag) =>
                    applyUrlPatch({ tags: optimistic.tags.filter((t) => t !== tag), page: 1 })
                  }
                  onClearAll={clearFilters}
                />
              </div>
            )}
          </div>
        </div>

        <TrendingTagsStrip
          onPickTag={(tag) => {
            setSearchInput("");
            applyUrlPatch({ q: "", tags: [tag], page: 1 }, ["tags"]);
            trackBrowseFilterApplied("tag", tag);
          }}
        />

        <div className="relative">
          {/* Subtle dot-matrix texture behind the catalog so the grid area
              doesn't read as a flat monotone block (the hero band above uses
              the grid pattern). Cards are opaque, so dots show only in gaps. */}
          <SectionBackground variant="dots" />
          <div className="relative z-10 container py-5">
          <div className="flex gap-6 items-start">
            <div
              id="browse-filter-rail"
              className={cn(
                "hidden lg:block shrink-0 transition-[width,opacity] duration-300 overflow-hidden",
                railCollapsed ? "w-0 opacity-0 pointer-events-none" : "w-[280px] opacity-100",
              )}
            >
              <BrowseFilters
                selectedCategory={optimistic.category}
                onCategoryChange={(slug) => applyUrlPatch({ category: slug }, ["category"])}
                license={optimistic.license}
                onLicenseChange={(license) => applyUrlPatch({ license }, ["license"])}
                priceRange={[optimistic.minPrice, optimistic.maxPrice]}
                onPriceRangeChange={(range) =>
                  applyUrlPatch({ minPrice: range[0], maxPrice: range[1] }, ["minPrice", "maxPrice"])
                }
                minRating={optimistic.minRating}
                onMinRatingChange={(minRating) => applyUrlPatch({ minRating }, ["minRating"])}
                selectedTags={optimistic.tags}
                onToggleTag={(tag) => {
                  applyUrlPatch({ tags: toggleBrowseTagList(optimistic.tags, tag), page: 1 }, ["tags"]);
                  trackBrowseFilterApplied("tag", tag);
                }}
                onClearTags={() => applyUrlPatch({ tags: [], page: 1 })}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </div>

            <div className="flex-1 min-w-0">
              {optimistic.category?.toLowerCase().includes("verse") && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
                  <Code2 className="h-4 w-4 text-primary shrink-0" />
                  <span>
                    Verse snippets include preview code. Look for the{" "}
                    <Code2 className="inline h-3.5 w-3.5 text-primary" /> icon on listings.
                  </span>
                </div>
              )}
              {committed.page === 1 && sponsoredCards.length > 0 && (
                <div className="mb-6">
                  <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Sponsored</h2>
                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                    {sponsoredCards.map((s) => (
                      <BrowseAssetCard
                        key={`sp-${s.asset.id}`}
                        asset={s.asset}
                        isSponsored
                        campaignId={s.campaignId}
                        onSponsoredClick={(cId) => recordClick({ campaignId: cId, placement: sponsoredPlacement })}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="mb-3 h-4">
                {isLoading ? (
                  <Skeleton className="h-4 w-36" />
                ) : paginatedAssets.length > 0 ? (
                  <p className="text-xs text-muted-foreground font-mono">
                    Page {committed.page} of {totalPages} · {totalAssets} asset{totalAssets === 1 ? "" : "s"}
                  </p>
                ) : null}
              </div>
              {isLoading && !assets ? (
                <div className={cn("grid gap-3", gridCols)}>
                  {[...Array(12)].map((_, i) => (
                    <BrowseProductCardSkeleton key={i} index={i} />
                  ))}
                </div>
              ) : error ? (
                <PageError
                  title="Failed to load assets"
                  description="We couldn't fetch the assets. This might be a temporary issue."
                  onRetry={() => refetch()}
                />
              ) : paginatedAssets.length > 0 ? (
                <>
                  <div className={cn("grid gap-3", gridCols)}>
                    {paginatedAssets.map((item, index) => (
                      <div key={item.asset.id}>
                        <BrowseAssetCard
                          asset={item.asset}
                          isTrending={trendingIds?.has(item.asset.id) || false}
                          isFounderProduct={(item.asset as any).seller?.is_early_founder || false}
                          position={index}
                        />
                      </div>
                    ))}
                  </div>
                  <BrowsePagination
                    currentPage={committed.page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalItems={totalCount}
                    pageSize={committed.perPage}
                    onPageSizeChange={(size) => applyUrlPatch({ perPage: size, page: 1 }, ["perPage"])}
                    pageSizeOptions={BROWSE_PAGE_SIZES}
                  />
                </>
              ) : (
                <BrowseEmptyState
                  hasFilters={hasActiveFilters}
                  onClearFilters={clearFilters}
                  restrictiveHints={restrictiveHints}
                  query={committed.q}
                  categorySlug={committed.category}
                />
              )}
            </div>
          </div>
          </div>
        </div>
      </PublicPageShell>

      <BackToTopButton />

      <Drawer direction="left" open={showMobileFilters} onOpenChange={setShowMobileFilters}>
        <DrawerContent className="fixed inset-y-0 left-0 right-auto mt-0 h-[100dvh] max-h-[100dvh] w-[min(100vw,320px)] rounded-none rounded-r-2xl border-y border-r border-border/50 p-0 overflow-y-auto">
          <DrawerHeader className="px-4 pt-6 pb-2 text-left border-b border-border/30">
            <DrawerTitle className="font-display text-lg">Filters</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-10">
            <BrowseFilters
              selectedCategory={optimistic.category}
              onCategoryChange={(slug) => applyUrlPatch({ category: slug })}
              license={optimistic.license}
              onLicenseChange={(license) => applyUrlPatch({ license })}
              priceRange={[optimistic.minPrice, optimistic.maxPrice]}
              onPriceRangeChange={(range) => applyUrlPatch({ minPrice: range[0], maxPrice: range[1] })}
              minRating={optimistic.minRating}
              onMinRatingChange={(minRating) => applyUrlPatch({ minRating })}
              selectedTags={optimistic.tags}
              onToggleTag={(tag) => {
                applyUrlPatch({ tags: toggleBrowseTagList(optimistic.tags, tag), page: 1 }, ["tags"]);
                trackBrowseFilterApplied("tag", tag);
              }}
              onClearTags={() => applyUrlPatch({ tags: [], page: 1 })}
              onClearFilters={() => {
                clearFilters();
                setShowMobileFilters(false);
              }}
              hasActiveFilters={hasActiveFilters}
              closeOnCategorySelect
              onCategoryPicked={() => setShowMobileFilters(false)}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
